import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';

interface SimpleTooltipProps {
  children: React.ReactNode;
  content: string;
  side?: 'top' | 'bottom' | 'left' | 'right' | 'auto';
  className?: string;
}

export const SimpleTooltip: React.FC<SimpleTooltipProps> = ({ 
  children, 
  content, 
  side = 'auto', 
  className 
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [actualSide, setActualSide] = useState<'top' | 'bottom' | 'left' | 'right'>('top');
  const triggerRef = useRef<HTMLDivElement>(null);

  const updatePosition = () => {
    if (!triggerRef.current) return;
    
    const triggerRect = triggerRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    let bestSide: 'top' | 'bottom' | 'left' | 'right' = side === 'auto' ? 'top' : side as any;
    
    if (side === 'auto') {
      // Smart positioning logic
      const spaceAbove = triggerRect.top;
      const spaceBelow = viewportHeight - triggerRect.bottom;
      const spaceLeft = triggerRect.left;
      const spaceRight = viewportWidth - triggerRect.right;
      
      // For elements near the left edge (likely near sidebar), prefer right positioning
      if (spaceLeft < 250 && spaceRight > 200) {
        bestSide = 'right';
      }
      // For elements near the right edge, prefer left positioning  
      else if (spaceRight < 200 && spaceLeft > 250) {
        bestSide = 'left';
      }
      // Otherwise use vertical positioning based on available space
      else if (spaceAbove > spaceBelow && spaceAbove > 100) {
        bestSide = 'top';
      } else if (spaceBelow > 100) {
        bestSide = 'bottom';
      }
      // Fallback to right if vertical space is limited
      else if (spaceRight > 200) {
        bestSide = 'right';
      } else {
        bestSide = 'left';
      }
    }
    
    setActualSide(bestSide);
    
    // Calculate tooltip position based on trigger element and chosen side
    let x = 0, y = 0;
    const offset = 8; // Distance from trigger element
    
    switch (bestSide) {
      case 'top':
        x = triggerRect.left + triggerRect.width / 2;
        y = triggerRect.top - offset;
        break;
      case 'bottom':
        x = triggerRect.left + triggerRect.width / 2;
        y = triggerRect.bottom + offset;
        break;
      case 'left':
        x = triggerRect.left - offset;
        y = triggerRect.top + triggerRect.height / 2;
        break;
      case 'right':
        x = triggerRect.right + offset;
        y = triggerRect.top + triggerRect.height / 2;
        break;
    }
    
    setPosition({ x, y });
  };

  useEffect(() => {
    if (isVisible) {
      updatePosition();

      // Update position on scroll or resize
      const handleUpdate = () => updatePosition();
      window.addEventListener('scroll', handleUpdate, true);
      window.addEventListener('resize', handleUpdate);

      return () => {
        window.removeEventListener('scroll', handleUpdate, true);
        window.removeEventListener('resize', handleUpdate);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, side]);

  const getTooltipTransform = () => {
    switch (actualSide) {
      case 'top':
        return 'translate(-50%, -100%)';
      case 'bottom':
        return 'translate(-50%, 0%)';
      case 'left':
        return 'translate(-100%, -50%)';
      case 'right':
        return 'translate(0%, -50%)';
      default:
        return 'translate(-50%, -100%)';
    }
  };

  const getArrowStyles = () => {
    const arrowSize = 4;
    switch (actualSide) {
      case 'top':
        return {
          position: 'absolute' as const,
          bottom: -arrowSize,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 0,
          height: 0,
          borderLeft: `${arrowSize}px solid transparent`,
          borderRight: `${arrowSize}px solid transparent`,
          borderTop: `${arrowSize}px solid rgb(17 24 39)`, // gray-900
        };
      case 'bottom':
        return {
          position: 'absolute' as const,
          top: -arrowSize,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 0,
          height: 0,
          borderLeft: `${arrowSize}px solid transparent`,
          borderRight: `${arrowSize}px solid transparent`,
          borderBottom: `${arrowSize}px solid rgb(17 24 39)`,
        };
      case 'left':
        return {
          position: 'absolute' as const,
          right: -arrowSize,
          top: '50%',
          transform: 'translateY(-50%)',
          width: 0,
          height: 0,
          borderTop: `${arrowSize}px solid transparent`,
          borderBottom: `${arrowSize}px solid transparent`,
          borderLeft: `${arrowSize}px solid rgb(17 24 39)`,
        };
      case 'right':
        return {
          position: 'absolute' as const,
          left: -arrowSize,
          top: '50%',
          transform: 'translateY(-50%)',
          width: 0,
          height: 0,
          borderTop: `${arrowSize}px solid transparent`,
          borderBottom: `${arrowSize}px solid transparent`,
          borderRight: `${arrowSize}px solid rgb(17 24 39)`,
        };
      default:
        return {};
    }
  };

  const tooltipPortal = isVisible && typeof window !== 'undefined' ? createPortal(
    <div
      style={{
        position: 'fixed',
        left: position.x,
        top: position.y,
        transform: getTooltipTransform(),
        zIndex: 9999,
      }}
      className="px-3 py-2 text-xs font-medium text-white bg-gray-900 dark:bg-gray-700 rounded-lg shadow-lg border border-gray-200 dark:border-gray-600 whitespace-nowrap animate-in fade-in-0 zoom-in-95 duration-200"
    >
      {content}
      <div style={getArrowStyles()}></div>
    </div>,
    document.body
  ) : null;

  return (
    <>
      <div 
        ref={triggerRef}
        className={cn("relative inline-block", className)}
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
      >
        {children}
      </div>
      {tooltipPortal}
    </>
  );
}; 